import { Component, ElementRef, inject, Input, OnChanges, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { BrandsEnum } from '../../../_enums/brands.enum';

const brandColorsName: { primary: string[], error: string[], success: string[], themes: string[] } = {
  primary: ['brand', 'light', 'base', 'hover', 'active', 'dark'],
  error: ['base', 'hover', 'active'],
  success: ['base', 'hover', 'active'],
  themes: [
    'very-light',
    'light',
    'regular',
    'colored',
    'promo-1',
    'promo-2',
    'stars',
    'extra',
    'dark'
  ]
} as const;

const themeVarsName: string[] = [
  '--brand--logo-color-background-brand',
  '--brand--logo-color-background-light',
  '--brand--logo-color-background-dark',
  '--brand--link-color',
  '--background-dark',
  '--brand--border-radius',
  '--brand--icon-border-radius',
  '--brand--button-color',
  '--brand--button-secondary-color',
  '--brand--button-secondary-border',
  '--brand--button-uppercase',
  '--brand--border-radius-global',
  '--brand--border-radius-global-large',
  '--brand--border-radius-tag',
  '--brand--border-radius-form',
  '--font-default',
  '--brand--font-family',
  '--color-tips',
  '--color-push-card-banner-dark',
  '--color-push-card-banner-button-dark',
  '--color-push-card-banner-button-text-dark',
  '--brand--horizontal-bar',
  '--brand--vertical-bar',
  '--brand--promo-banner',
  '--color-price-discounted'
] as const;

@Component({
  template: '',
  standalone: true
})
export abstract class SdThemeComponent implements OnChanges {
  @Input()
  brand?: BrandsEnum;

  @Input()
  activeBrand?: BrandsEnum;

  protected elRef = inject(ElementRef);
  protected document = inject(DOCUMENT);
  protected platformId = inject(PLATFORM_ID);

  ngOnChanges(changes: SimpleChanges): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.isThemeChanged(changes)) {
        this.changeTheme(this.brand);
      }
    }
  }

  updateMainBackground(cssVar: string, selector?: string, cssVarProperty?: boolean) {
    if (isPlatformBrowser(this.platformId)) {
      const activeBrandName = this.activeBrand ? '-' + this.activeBrand.toLowerCase() : '';
      const cssVarValue = getComputedStyle(this.document.documentElement).getPropertyValue(`${cssVar}${activeBrandName}`);
      const propertyName = cssVarProperty ? cssVar : 'background';

      if (selector) {
        this.elRef.nativeElement.querySelectorAll(selector).forEach((element: HTMLElement) => {
          element.style.setProperty(propertyName, cssVarValue);
        });
      } else {
        this.elRef.nativeElement.style.setProperty(propertyName, cssVarValue);
      }
    }
  }

  private changeTheme(brand?: BrandsEnum): void {
    const computedStyle = getComputedStyle(this.document.documentElement);
    const brandName = brand ? '-' + brand.toLowerCase() : '';
    const cssProperties: Record<string, string> = {};

    Object.entries(brandColorsName).forEach(([key, values]) => {
      values.forEach((colorName) => {
        cssProperties[`--color-${key}-${colorName}`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}`);
        cssProperties[`--color-${key}-${colorName}-h`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-h`);
        cssProperties[`--color-${key}-${colorName}-s`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-s`);
        cssProperties[`--color-${key}-${colorName}-l`] = computedStyle.getPropertyValue(`--color-${key}-${colorName}${brandName}-l`);
      });
    });

    themeVarsName.forEach((cssVar) => {
      const cssVarValue = computedStyle.getPropertyValue(`${cssVar}${brandName}`);
      cssProperties[cssVar] = cssVarValue;
    });

    Object.entries(cssProperties).forEach(([property, value]) => {
      this.elRef.nativeElement.style.setProperty(property, value);
    });
  }

  private isThemeChanged({ brand }: SimpleChanges): boolean {
    const { previousValue, currentValue } = brand ?? {};
    return (!previousValue !== !currentValue) && (previousValue !== currentValue);
  }
}
